import { LoadingState } from "@shared/common/components/State/LoadingState";
import { useActionQuery } from "../../SBDAction/useActionQuery";
import { ErrorState } from "@shared/common/components/State/ErrorState";
import { Action } from "@opstel/query/v2/models/action";
import { ActionType } from "@opstel/query/v2/models/actionType";
import { AllowedActionMutations } from "@opstel/query/v2/models/allowedActionMutations";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { useActionMutation } from "../../SBDAction/useActionMutation";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";
import { useAllowedActionMutationsQuery } from "../../SBDAction/useAllowedActionMutationsQuery";
import { RouteSelection } from "./RouteSelection";
import { TimeSelection } from "./TimeSelection";

interface EditDataProviderProps {
  readonly actionId: string;
  readonly onClose: () => void;
}

export function EditDataProvider({ actionId, onClose }: EditDataProviderProps) {
  const {
    data: action,
    isPending: actionPending,
    isError: actionError,
  } = useActionQuery({ actionId });
  const {
    data: allowedActionMutations,
    isPending: allowedActionMutationsPending,
    isError: allowedActionMutationsError,
  } = useAllowedActionMutationsQuery({ actionId });

  if (actionPending || allowedActionMutationsPending) return <LoadingState />;
  if (actionError || allowedActionMutationsError) return <ErrorState />;

  return <Edit action={action} allowedActionMutations={allowedActionMutations} onClose={onClose} />;
}

interface EditProps {
  readonly action: Action;
  readonly allowedActionMutations: AllowedActionMutations;
  readonly onClose: () => void;
}

export function Edit({ action, allowedActionMutations, onClose }: EditProps) {
  const { mutate } = useActionMutation({ actionId: action.id });

  const [startTime, setStartTime] = useState<DateTime<true> | undefined>(action.startTime);
  const [routeId, setRouteId] = useState<string | undefined>(allowedActionMutations.currentRouteId);

  const handleSubmit = useCallback<() => boolean>(() => {
    if (!startTime) {
      messageToast({
        type: "error",
        message: "Vertrektijd is niet goed ingevoerd (00:00.0)",
      });

      return false;
    }

    mutate({
      ...(startTime.equals(action.startTime) ? {} : { startTime }),
      ...(routeId === allowedActionMutations.currentRouteId ? {} : { routeId }),
    });

    return true;
  }, [action, allowedActionMutations, startTime, routeId, mutate]);

  const handleSubmitAndClose = useCallback(() => {
    const success = handleSubmit();

    if (success) {
      onClose();
    }
  }, [handleSubmit, onClose]);

  return (
    <>
      {/* Replace with tractie-section-header when released */}
      <div slot="start" className="flex h-8 items-center">
        <h3 className="m-0">{`${ActionType.getDutchTranslation(action.type)} wijzigen`}</h3>
      </div>

      <tractie-form>
        <form className="tractie-form-layout">
          <tractie-layout row-gap="l">
            <TimeSelection
              dateLabel="Vertrekdatum"
              timeLabel="Vertrektijd"
              currentTime={action.startTime}
              allowedTimeInterval={allowedActionMutations.allowedStartTimeInterval}
              onTimeChange={setStartTime}
            />
            <RouteSelection
              routes={allowedActionMutations.allowedRoutes}
              currentRouteId={allowedActionMutations.currentRouteId}
              onRouteChange={setRouteId}
            />
          </tractie-layout>
        </form>
      </tractie-form>

      <footer slot="end" className="flex flex-col gap-s">
        <tractie-divider className="-mx-(--_container-padding-inline-start)" />
        <tractie-button
          data-testid="sbd-sidepanel-edit-save-close-button"
          level="primary"
          onClick={handleSubmitAndClose}
        >
          Opslaan en sluiten
        </tractie-button>
        <div className="flex gap-s">
          <tractie-button
            data-testid="sbd-sidepanel-edit-save-button"
            level="secondary"
            className="w-1/2"
            onClick={handleSubmit}
          >
            Opslaan
          </tractie-button>
          <tractie-button
            data-testid="sbd-sidepanel-edit-cancel-button"
            level="secondary"
            className="w-1/2"
            onClick={onClose}
          >
            Annuleren
          </tractie-button>
        </div>
      </footer>
    </>
  );
}
